import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon, CheckCircleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const { register: registerUser, resendVerification } = useAuth();

  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const password = watch('password');

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await registerUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        companyName: data.companyName,
      });
      if (result.success) {
        setRegisteredEmail(data.email);
        setRegistrationComplete(true);
      }
    } catch (error) {
      console.error('Registration error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center px-4">
        <div className="w-full max-w-md space-y-6">
          <div className="flex justify-center">
            <Logo className="h-10" showText={true} variant="dark" />
          </div>
          <div className="bg-white rounded-xl shadow-lg p-8 border border-neutral-200 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                <EnvelopeIcon className="w-8 h-8 text-primary-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-neutral-800">Check your email</h2>
            <p className="mt-2 text-neutral-500">
              We've sent a verification link to <span className="font-medium text-neutral-700">{registeredEmail}</span>.
              Please click the link to activate your account.
            </p>
            <div className="mt-6 space-y-3">
              <button onClick={() => resendVerification(registeredEmail)} className="btn-secondary w-full">
                Resend verification email
              </button>
              <Link to="/login" className="btn-outline w-full inline-block">
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-100 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-2/5 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-30" />
        <div className="absolute top-1/3 -left-20 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -right-20 w-64 h-64 bg-primary-600/10 rounded-full blur-3xl" />
        <svg className="absolute inset-0 w-full h-full opacity-20" xmlns="http://www.w3.org/2000/svg">
          <defs><pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse"><circle cx="20" cy="20" r="1" fill="#FF7900" opacity="0.5" /></pattern></defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
        <div className="relative z-10 flex flex-col justify-center px-10 xl:px-16">
          <div className="mb-10"><Logo className="h-10" showText={true} variant="light" /></div>
          <div className="space-y-6 max-w-md">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight">
              Start building<span className="block text-primary-500">[ powerful ]</span>client experiences
            </h1>
            <p className="text-base text-neutral-400 leading-relaxed">Join businesses using Chatza to automate and enhance their client communications.</p>
            <div className="pt-6 space-y-3">
              {['Team collaboration built-in', 'Automated chatbot workflows', 'Setup in under 5 minutes'].map((b, i) => (
                <div key={i} className="flex items-center gap-3 text-neutral-300">
                  <CheckCircleIcon className="w-5 h-5 text-primary-500 flex-shrink-0" /><span className="text-sm">{b}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="absolute bottom-8 left-10 xl:left-16 flex items-center gap-2 text-neutral-500 text-xs">
            <div className="w-6 h-px bg-primary-500" /><span>Enterprise-grade security</span>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-8">
        <div className="w-full max-w-lg space-y-6">
          <div className="lg:hidden flex justify-center mb-6"><Logo className="h-10" showText={true} variant="dark" /></div>
          <div className="text-center lg:text-left">
            <h2 className="text-2xl font-bold text-neutral-800">Create your account</h2>
            <p className="mt-1 text-neutral-500 text-sm">Register your organization on Chatza</p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border border-neutral-200">
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label className="input-label">Organization / Company Name</label>
                <input {...register('companyName', { required: 'Organization name is required', minLength: { value: 2, message: 'Min 2 characters' } })} type="text" className={`input-field ${errors.companyName ? 'input-error' : ''}`} placeholder="Your company or organization" />
                {errors.companyName && <p className="input-error-message">{errors.companyName.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">First Name</label>
                  <input {...register('firstName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })} type="text" className={`input-field ${errors.firstName ? 'input-error' : ''}`} placeholder="John" />
                  {errors.firstName && <p className="input-error-message">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label className="input-label">Last Name</label>
                  <input {...register('lastName', { required: 'Required', minLength: { value: 2, message: 'Min 2 chars' } })} type="text" className={`input-field ${errors.lastName ? 'input-error' : ''}`} placeholder="Doe" />
                  {errors.lastName && <p className="input-error-message">{errors.lastName.message}</p>}
                </div>
              </div>

              <div>
                <label className="input-label">Email Address</label>
                <input {...register('email', { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email' } })} type="email" autoComplete="email" className={`input-field ${errors.email ? 'input-error' : ''}`} placeholder="name@company.com" />
                {errors.email && <p className="input-error-message">{errors.email.message}</p>}
              </div>

              <div>
                <label className="input-label">Phone <span className="text-neutral-400 font-normal">(Optional)</span></label>
                <input {...register('phone')} type="tel" className="input-field" placeholder="+27 12 345 6789" />
              </div>

              <div>
                <label className="input-label">Password</label>
                <div className="relative">
                  <input {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'Min 8 characters' } })} type={showPassword ? 'text' : 'password'} autoComplete="new-password" className={`input-field pr-12 ${errors.password ? 'input-error' : ''}`} placeholder="Create a strong password" />
                  <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && <p className="input-error-message">{errors.password.message}</p>}
              </div>

              <div>
                <label className="input-label">Confirm Password</label>
                <div className="relative">
                  <input {...register('confirmPassword', { required: 'Please confirm', validate: v => v === password || 'Passwords do not match' })} type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" className={`input-field pr-12 ${errors.confirmPassword ? 'input-error' : ''}`} placeholder="Confirm your password" />
                  <button type="button" className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="input-error-message">{errors.confirmPassword.message}</p>}
              </div>

              <div className="flex items-start gap-3">
                <input id="terms" type="checkbox" required className="mt-0.5 w-4 h-4 rounded border-neutral-300 text-primary-500 focus:ring-primary-500 focus:ring-offset-0" />
                <label htmlFor="terms" className="text-sm text-neutral-600">
                  I agree to the <button type="button" className="text-primary-500 hover:text-primary-600 font-medium">Terms of Service</button> and <button type="button" className="text-primary-500 hover:text-primary-600 font-medium">Privacy Policy</button>
                </label>
              </div>

              <button type="submit" disabled={isLoading} className="btn-primary w-full">
                {isLoading ? (<div className="flex items-center justify-center gap-2"><div className="spinner spinner-sm border-white/30 border-t-white" /><span>Creating account...</span></div>) : 'Create Account'}
              </button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200" /></div>
              <div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-neutral-400">Already have an account?</span></div>
            </div>
            <Link to="/login" className="btn-secondary w-full">Sign in instead</Link>
          </div>
          <p className="text-center text-xs text-neutral-400">&copy; 2025 Chatza. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Register;
